import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { get } from '../services/api/axios.config';

export const BotContext = createContext({});

const detectBotType = (tenantId) => {
  if (!tenantId) return 'sac';
  return String(tenantId).toLowerCase().includes('leads') ? 'sales' : 'sac';
};

const BOT_TYPE_LABELS = {
  sales: 'Bot de Vendas',
  sac: 'Bot de SAC',
};

// Gera os IDs candidatos a partir do tenant principal (convenção de nomenclatura)
const getCandidateIds = (tenantId) => [
  `${tenantId}_leads`,
];

// Tenta acessar um endpoint do tenant para ver se o token tem permissão
const probeAccess = async (tenantId) => {
  try {
    await get(`/api/tenant/${tenantId}/conversations?limit=1`);
    return true;
  } catch (err) {
    return false;
  }
};

export const BotProvider = ({ children }) => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [accessibleBots, setAccessibleBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user && user.type !== 'admin') {
      loadBots();
    } else {
      setAccessibleBots([]);
      setSelectedBotId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  const loadBots = async () => {
    const tenantId = user?.tenantId || user?.id;
    if (!tenantId) return;

    setLoading(true);
    try {
      const bots = [{
        id: tenantId,
        name: user?.name || tenantId,
        type: detectBotType(tenantId),
        typeLabel: BOT_TYPE_LABELS[detectBotType(tenantId)],
        isMain: true,
      }];

      // Sonda os candidatos para descobrir bots acessíveis
      const candidates = getCandidateIds(tenantId);
      for (const candidateId of candidates) {
        const accessible = await probeAccess(candidateId);
        if (accessible) {
          bots.push({
            id: candidateId,
            // Usa o mesmo nome do tenant principal (mesma empresa)
            name: user?.name || tenantId,
            type: detectBotType(candidateId),
            typeLabel: BOT_TYPE_LABELS[detectBotType(candidateId)],
            isMain: false,
          });
          console.log('✅ Bot acessível encontrado:', candidateId);
        }
      }

      console.log('🤖 BotContext | bots:', bots.map((b) => `${b.id} (${b.type})`));

      setAccessibleBots(bots);
      setSelectedBotId((prev) => prev || tenantId);
    } catch (error) {
      console.error('Erro ao carregar bots:', error);
      const fallbackId = user?.tenantId || user?.id;
      setAccessibleBots([{
        id: fallbackId,
        name: user?.name || fallbackId,
        type: detectBotType(fallbackId),
        typeLabel: BOT_TYPE_LABELS[detectBotType(fallbackId)],
        isMain: true,
      }]);
      setSelectedBotId((prev) => prev || fallbackId);
    } finally {
      setLoading(false);
    }
  };

  const selectedBot = accessibleBots.find((b) => b.id === selectedBotId) || null;

  const selectBot = useCallback((botId) => {
    setSelectedBotId(botId);
  }, []);

  return (
    <BotContext.Provider value={{
      accessibleBots,
      selectedBotId,
      selectedBot,
      isSalesBot: selectedBot?.type === 'sales',
      hasMultipleBots: accessibleBots.length > 1,
      loading,
      selectBot,
    }}>
      {children}
    </BotContext.Provider>
  );
};
